/**
 * t611u: the workspace's own wording for hint lines (GET /workspaces/:ws/text-overrides), fetched once per workspace
 * and kept with the rest of the workspace's start-up data. `useHintEditing` is the page's "Edit hints" switch.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import type { TextOverridesView } from '@tradeoutlet/shared';
import { api } from './api';
import { useWorkspaceId } from './hooks';

export const textOverridesKey = (ws: string) => ['text-overrides', ws] as const;

export function useTextOverrides() {
  const ws = useWorkspaceId();
  const q = useQuery({ queryKey: textOverridesKey(ws ?? ''), queryFn: () => api.textOverrides(ws!), enabled: Boolean(ws), staleTime: 5 * 60_000 });
  const map = useMemo(() => new Map((q.data?.overrides ?? []).map(o => [o.key, o.text])), [q.data]);
  return { ws, data: q.data as TextOverridesView | undefined, map, canEdit: Boolean(q.data?.can_edit), isLoading: q.isLoading };
}

/** The text to show for a hint: the workspace's own wording if it has one, else the default. */
export function useHintText(key: string, fallback: string): string {
  return useTextOverrides().map.get(key) ?? fallback;
}

interface HintEditing { on: boolean; set: (on: boolean) => void }
export const useHintEditing = create<HintEditing>(set => ({ on: false, set: on => set({ on }) }));
